import React, { useState, useEffect } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { V1 } from '@frontend/api-frontend-gateway';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Heading,
  Text,
  ContentLoader,
  TextLink,
  Table,
  Button,
  Chip,
  Modal,
  useModalControl,
  TextField,
  useAlert,
} from '@frontend/design-system';

type SiteInfo = NonNullable<V1.Types.GetSiteConfigIO['output']['siteConfig']>;

const useSiteData = (site: string) => {
  const result = V1.Queries.useGetSiteConfigQuery({
    request: {
      siteName: site,
    },
    options: {
      retry: 2,
    },
  });

  return result;
};

const useSiteReleases = (site: string) => {
  const result = V1.Queries.useGetPublishedSiteConfigsQuery({
    request: {
      siteName: site,
    },
    options: {
      retry: 2,
    },
  });

  return result;
};

/* Since we don't have a direct way to see the production site name from the edge site, we derive it here */
/* IMPORTANT IF YOU ADD NEW SITE WITH EDGE YOU NEED TO ADD IT TO THIS LIST, UPDATE THIS FUNCTION */
const getProductionSiteName = (site: string): string | null => {
  if (site === 'edge.getweave.com') {
    return 'app.getweave.com';
  } else if (site === 'deployment-playground-edge.weavelab.ninja') {
    return 'deployment-playground.weavelab.ninja';
  } else {
    return null;
  }
};

const useMarkBuildsUnpromotable = () => {
  return V1.Queries.useFetchMarkSiteBuildsAsUnpromotable();
};

const useUpdateSiteConfig = () => {
  return V1.Queries.useFetchPublishSiteConfig();
};

type MetadataEntry = {
  key: string;
  value: string;
  id: string; // Add unique ID for form fields
};

const UpdateMetadataModal = ({
  show,
  onClose,
  currentMetadata,
  onSave,
  isLoading = false,
  configVersion,
}: {
  show: boolean;
  onClose: () => void;
  currentMetadata?: Record<string, any>;
  onSave: (metadata: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
  configVersion?: string;
}) => {
  const [metadataEntries, setMetadataEntries] = useState<MetadataEntry[]>(() => {
    if (currentMetadata) {
      return Object.entries(currentMetadata).map(([key, value], index) => ({
        key,
        value: String(value),
        id: `entry-${index}`,
      }));
    }
    return [{ key: '', value: '', id: 'entry-0' }];
  });

  // Update metadata entries when modal opens with new current metadata
  useEffect(() => {
    if (show && currentMetadata) {
      setMetadataEntries(
        Object.entries(currentMetadata).map(([key, value], index) => ({
          key,
          value: String(value),
          id: `entry-${index}`,
        }))
      );
    } else if (show && !currentMetadata) {
      // If no current metadata, start with empty entry
      setMetadataEntries([{ key: '', value: '', id: 'entry-0' }]);
    }
  }, [show, currentMetadata]);

  const addEntry = () => {
    const newId = `entry-${Date.now()}`;
    setMetadataEntries([...metadataEntries, { key: '', value: '', id: newId }]);
  };

  const removeEntry = (index: number) => {
    setMetadataEntries(metadataEntries.filter((_, i) => i !== index));
  };

  const updateEntry = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...metadataEntries];
    updated[index][field] = newValue;
    setMetadataEntries(updated);
  };

  const handleSave = async () => {
    const metadata = metadataEntries
      .filter((entry) => entry.key.trim() !== '')
      .reduce((acc, entry) => {
        acc[entry.key] = entry.value;
        return acc;
      }, {} as Record<string, string>);

    //TODO: uncomment once ACLS are in place
    await onSave(metadata);
    onClose();
  };

  const handleCancel = () => {
    // Reset to original metadata
    if (currentMetadata) {
      setMetadataEntries(
        Object.entries(currentMetadata).map(([key, value], index) => ({
          key,
          value: String(value),
          id: `entry-${index}`,
        }))
      );
    } else {
      setMetadataEntries([{ key: '', value: '', id: 'entry-0' }]);
    }
    onClose();
  };

  return (
    <Modal show={show} onClose={handleCancel}>
      <Modal.Header>
        <Heading level={2}>Update Metadata</Heading>
        {configVersion && (
          <Text color='subdued' css={{ marginTop: theme.spacing(1) }}>
            Config version: {configVersion.substring(0, 8)}
          </Text>
        )}
      </Modal.Header>
      <Modal.Body>
        <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
          <div>
            <Text color='subdued'>Add or modify metadata key-value pairs for this site configuration.</Text>
            {currentMetadata && Object.keys(currentMetadata).length > 0 && (
              <Text color='subdued' css={{ marginTop: theme.spacing(1), fontSize: theme.fontSize(12) }}>
                Currently has {Object.keys(currentMetadata).length} metadata entr
                {Object.keys(currentMetadata).length === 1 ? 'y' : 'ies'}
              </Text>
            )}
          </div>

          {metadataEntries.map((entry, index) => (
            <div key={entry.id} css={{ display: 'flex', gap: theme.spacing(2), alignItems: 'end' }}>
              <div css={{ flex: 1 }}>
                <TextField
                  name={`key-${entry.id}`}
                  label='Key'
                  placeholder='Enter metadata key'
                  disabled={isLoading}
                  value={entry.key}
                  onChange={(event) => {
                    const target = (event as React.ChangeEvent<HTMLInputElement>)?.target;
                    if (target) {
                      updateEntry(index, 'key', target.value || '');
                    }
                  }}
                  id={`key-${entry.id}`}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  active={false}
                  error=''
                  aria-invalid={false}
                  touched={false}
                />
              </div>
              <div css={{ flex: 1 }}>
                <TextField
                  name={`value-${entry.id}`}
                  label='Value'
                  placeholder='Enter metadata value'
                  disabled={isLoading}
                  value={entry.value}
                  onChange={(event) => {
                    const target = (event as React.ChangeEvent<HTMLInputElement>)?.target;
                    if (target) {
                      updateEntry(index, 'value', target.value || '');
                    }
                  }}
                  id={`value-${entry.id}`}
                  onBlur={() => {}}
                  onFocus={() => {}}
                  active={false}
                  error=''
                  aria-invalid={false}
                  touched={false}
                />
              </div>
              <Button
                variant='tertiary'
                size='small'
                onClick={() => removeEntry(index)}
                disabled={metadataEntries.length === 1 || isLoading}
                css={{ marginBottom: theme.spacing(1) }}
              >
                <Icon name='delete' />
              </Button>
            </div>
          ))}

          <Button variant='tertiary' size='small' onClick={addEntry}>
            <Icon name='add' /> Add Entry
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save Metadata'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const SiteDetails = ({ site }: { site: string }) => {
  const canEdit = hasSchemaACL('weave', Permission.APP_RELEASE_WRITE);
  const { data: siteConfigResponse, isLoading, error, refetch: refetchSiteConfig } = useSiteData(site);
  const { data: siteReleasesResponse, isLoading: releasesLoading, refetch: refetchReleases } = useSiteReleases(site);
  const updateSiteConfig = useUpdateSiteConfig();
  const updateBuildPromotable = useMarkBuildsUnpromotable();
  const alerts = useAlert();
  const metadataModal = useModalControl();
  const [selectedConfig, setSelectedConfig] = useState<SiteInfo | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  /* This function handles marking a build as unpromotable */
  const handleMarkingUnPromotable = async (config: SiteInfo) => {
    setIsUpdating(true);
    try {
      await updateBuildPromotable({
        request: {
          siteName: site,
          bucketPrefix: config.bucketPrefix,
          bucketName: config.bucketName,
        },
      });

      console.log(
        `Successfully marked config ${config.version} as ${!config.skipPromotion ? 'promotable' : 'unpromotable'}`
      );

      // Refresh data to show updated status
      await Promise.all([refetchSiteConfig(), refetchReleases()]);
    } catch (error) {
      alerts.error('Failed to update promotable status. Please try again.');
      console.error('Failed to update promotable status:', error);
    } finally {
      alerts.success('Promotable status updated successfully.');
      setIsUpdating(false);
    }
  };

  const handlePromoteBuild = async (config: SiteInfo, promoteToSite: string) => {
    if (!config || !promoteToSite) return;
    setIsUpdating(true);
    try {
      await updateSiteConfig({
        request: {
          ...config,
          siteName: promoteToSite,
        },
      });

      console.log(`Successfully promoted config ${config.version} to site ${promoteToSite}`);

      // Refresh data to show updated status
      await Promise.all([refetchSiteConfig(), refetchReleases()]);
    } catch (error) {
      alerts.error('Failed to promote release. Please try again.');
      console.error('Failed to promote release:', error);
    } finally {
      alerts.success('Promotion request submitted successfully. Go to the production site to see the new release.');
      setIsUpdating(false);
    }
  };

  const handleUpdateMetadata = (config: SiteInfo) => {
    setSelectedConfig(config);
    metadataModal.openModal();
  };

  const handleSaveMetadata = async (metadata: Record<string, any>) => {
    if (!selectedConfig) return;

    setIsUpdating(true);
    try {
      // Create the update payload with all existing config data plus new metadata
      const updatePayload = {
        ...selectedConfig,
        metadata: metadata, // Use the new metadata
      };

      await updateSiteConfig({
        request: {
          siteName: site,
          ...updatePayload,
        },
      });

      console.log('Successfully updated metadata for config:', selectedConfig.version);

      // Refresh data to show updated metadata
      await Promise.all([refetchSiteConfig(), refetchReleases()]);
    } catch (error) {
      console.error('Failed to update metadata:', error);
      alerts.error('Failed to update metadata. Please try again.');
    } finally {
      alerts.success('Metadata updated successfully.');
      setIsUpdating(false);
      setSelectedConfig(null);
    }
  };

  if (isLoading) {
    return (
      <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(12) }}>
        <ContentLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div css={{ padding: theme.spacing(6), textAlign: 'center' }}>
        <Text color='error'>Failed to load site configuration</Text>
      </div>
    );
  }

  if (!siteConfigResponse?.siteConfig) {
    return (
      <div css={{ padding: theme.spacing(6), textAlign: 'center' }}>
        <Text color='subdued'>No site configuration found</Text>
      </div>
    );
  }

  return (
    <div css={{ padding: theme.spacing(6) }}>
      <div css={{ maxWidth: '800px' }}>
        <SiteConfigInfo
          siteConfig={siteConfigResponse.siteConfig}
          onUpdateMetadata={handleUpdateMetadata}
          canEdit={canEdit}
        />
      </div>
      {releasesLoading ? (
        <div css={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(6) }}>
          <ContentLoader />
        </div>
      ) : (
        <SiteReleasesTable
          siteReleases={siteReleasesResponse?.publishedSiteConfigs}
          currentConfig={siteConfigResponse.siteConfig}
          onUpdatePromotable={handleMarkingUnPromotable}
          onPromoteBuild={handlePromoteBuild}
          onUpdateMetadata={handleUpdateMetadata}
          canEdit={canEdit}
        />
      )}

      <UpdateMetadataModal
        {...metadataModal.modalProps}
        currentMetadata={selectedConfig?.metadata}
        onSave={handleSaveMetadata}
        isLoading={isUpdating}
        configVersion={selectedConfig?.version}
      />
    </div>
  );
};

const SiteReleasesTable = ({
  siteReleases,
  currentConfig,
  onUpdateMetadata,
  onPromoteBuild,
  onUpdatePromotable,
  canEdit,
}: {
  siteReleases: V1.Types.GetPublishedSiteConfigsIO['output']['publishedSiteConfigs'];
  currentConfig: SiteInfo;
  onUpdateMetadata: (config: SiteInfo) => void;
  onPromoteBuild: (config: SiteInfo, promoteToSite: string) => void;
  onUpdatePromotable: (config: SiteInfo) => void;
  canEdit: boolean;
}) => {
  if (!siteReleases || siteReleases.length === 0) {
    return (
      <ConfigSection title='Previous Releases'>
        <div css={{ padding: theme.spacing(4), textAlign: 'center' }}>
          <Text color='subdued'>No previous releases found</Text>
        </div>
      </ConfigSection>
    );
  }

  return (
    <ConfigSection title='Previous Releases'>
      <div
        css={{
          backgroundColor: theme.colors.white,
          borderRadius: theme.borderRadius.medium,
          width: '100%',
          overflow: 'hidden',
          overflowX: 'auto',
        }}
      >
        <Table
          data={siteReleases}
          styleConfig={{
            rows: (rowData) =>
              rowData.version === currentConfig.version
                ? {
                    backgroundColor: theme.colors.success5,
                    '&:hover': {
                      backgroundColor: theme.colors.success10,
                    },
                  }
                : undefined,
          }}
          colConfig={[
            {
              Header: '#',
              id: 'index',
              accessor: (_, index) => index + 1,
              maxWidth: 60,
            },
            {
              Header: 'Version',
              id: 'version',
              accessor: (release) => {
                const metadata = release.metadata;
                let summaryText = 'Summary unavailable';
                if (metadata) {
                  const commitMessage = metadata.commit_message?.replace(/"/g, '') || 'No commit message';
                  const author = metadata.commit_author?.replace(/"/g, '') || 'Unknown author';
                  const prNumber = metadata.pr_number;

                  if (prNumber && prNumber !== '' && prNumber !== 'No PR') {
                    summaryText = `${commitMessage} by ${author} • PR #${prNumber}`;
                  } else {
                    summaryText = `${commitMessage} by ${author}`;
                  }
                }

                return (
                  <div>
                    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                      <TextLink
                        href={`https://github.com/weave-lab/frontend/commit/${release.version}`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        {release.version?.substring(0, 8) || 'N/A'}
                      </TextLink>
                      {release.version === currentConfig.version && (
                        <Chip variant='success' size='default'>
                          Current
                        </Chip>
                      )}
                    </div>
                    <div css={{ marginTop: theme.spacing(0.5) }}>
                      <Text
                        color='subdued'
                        css={{
                          fontSize: theme.fontSize(12),
                          fontStyle: 'italic',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: '1.4',
                        }}
                      >
                        {summaryText}
                      </Text>
                    </div>
                  </div>
                );
              },
              minWidth: 300,
            },
            {
              Header: 'Site Version',
              id: 'metadataVersion',
              accessor: (release) => {
                const version = release.metadata?.version?.replace(/"/g, '');
                return version && version !== 'undefined' ? version : 'N/A';
              },
              maxWidth: 120,
            },
            {
              Header: 'Build Timestamp',
              id: 'buildTimestamp',
              accessor: (release) => {
                const timestamp = release.metadata?.timestamp?.replace(/"/g, '');
                if (!timestamp) return 'N/A';

                // Parse timestamp format like "2025-09-24_16:10:36"
                try {
                  const formattedTimestamp = timestamp.replace(/_/, ' ');
                  const date = new Date(formattedTimestamp);
                  if (isNaN(date.getTime())) {
                    return timestamp; // Return raw if can't parse
                  }
                  return date.toLocaleString();
                } catch {
                  return timestamp; // Return raw if parsing fails
                }
              },
              minWidth: 150,
            },
            {
              Header: 'Published At',
              id: 'publishedAt',
              accessor: (release) => {
                if (!release.publishedAt) return 'N/A';
                return new Date(release.publishedAt).toLocaleString();
              },
            },
            {
              Header: 'Urgency',
              id: 'urgency',
              accessor: (release) => release.publicMetadata?.urgency || 'N/A',
              maxWidth: 80,
            },
            {
              Header: 'Promotable',
              id: 'promotable',
              accessor: (release) => {
                // Check if the release has skipPromotion flag, defaulting to promotable if not found
                const isPromotable = !release.skipPromotion;
                return (
                  <Chip variant={isPromotable ? 'success' : 'warn'} size='default'>
                    {isPromotable ? 'Promotable' : 'Unpromotable'}
                  </Chip>
                );
              },
            },
            {
              Header: 'Promoter',
              id: 'promoter',
              accessor: (release) => {
                const promoter = release.metadata?.promoter || 'N/A';
                const isManual = promoter === 'manual';

                if (promoter === 'N/A') {
                  return <Text color='subdued'>N/A</Text>;
                }

                return (
                  <Chip variant={isManual ? 'warn' : 'success'} size='default'>
                    {promoter}
                  </Chip>
                );
              },
            },
            {
              Header: 'Bucket Prefix',
              id: 'bucketPrefix',
              accessor: (release) => (
                <TextLink
                  href={`https://console.cloud.google.com/storage/browser/${release.bucketName}/${release.bucketPrefix}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {release.bucketPrefix || 'N/A'}
                </TextLink>
              ),
            },
          ]}
          rowActions={{
            actions: [
              {
                Icon: () => <Icon name='update' />,
                label: 'Roll back to this release',
                onClick: (release) => {
                  console.log('Rolling back to release:', release);
                  // TODO: Implement rollback functionality
                },
                hide: (release) => release.version === currentConfig.version || !canEdit,
              },
              {
                Icon: () => <Icon name='upload' />,
                label: 'Promote Release',
                onClick: (release) => {
                  const productionSite = getProductionSiteName(release.siteName || '');
                  if (!productionSite) return;
                  onPromoteBuild(release, productionSite);
                },
                hide: (release) =>
                  (release.siteName && !release.siteName.includes('edge')) || release.skipPromotion || !canEdit,
              },
              {
                Icon: () => <Icon name='edit' />,
                label: 'Mark unpromotable',
                onClick: (release) => {
                  onUpdatePromotable(release);
                },
                hide: (release) => release.version === currentConfig.version || !canEdit || release.skipPromotion,
              },
              {
                Icon: () => <Icon name='settings' />,
                label: 'Update metadata',
                onClick: (release) => {
                  onUpdateMetadata(release);
                },
                hide: () => !canEdit,
              },
            ],
          }}
          isPaginated
          hasGlobalSearch
          clientPaginationConfig={{ defaultRowsPerPage: 30 }}
        />
      </div>
    </ConfigSection>
  );
};

const SiteConfigInfo = ({
  siteConfig,
  onUpdateMetadata,
  canEdit,
}: {
  siteConfig: SiteInfo;
  onUpdateMetadata: (config: SiteInfo) => void;
  canEdit: boolean;
}) => {
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  return (
    <div css={{ marginBottom: theme.spacing(6) }}>
      <div
        css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(4) }}
      >
        <Heading level={3} css={{ color: theme.colors.neutral70 }}>
          Site Configuration
        </Heading>
        {canEdit && (
          <Button variant='primary' size='small' onClick={() => onUpdateMetadata(siteConfig)}>
            <Icon name='settings' /> Update Metadata
          </Button>
        )}
      </div>

      {/* Primary Info Tiles */}
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: theme.spacing(2),
          marginBottom: theme.spacing(3),
        }}
      >
        <InfoTile
          title='Site Name'
          value={siteConfig.siteName}
          href={siteConfig.siteName ? `https://${siteConfig.siteName}` : undefined}
        />
        <InfoTile
          title='Version'
          value={siteConfig.version ? siteConfig.version.substring(0, 8) : undefined}
          href={siteConfig.version ? `https://github.com/weave-lab/frontend/commit/${siteConfig.version}` : undefined}
        />
        <InfoTile
          title='Published At'
          value={siteConfig.publishedAt ? new Date(siteConfig.publishedAt).toLocaleString() : undefined}
        />
        <InfoTile
          title='Bucket Prefix'
          value={siteConfig.bucketPrefix}
          href={
            siteConfig.bucketPrefix
              ? `https://console.cloud.google.com/storage/browser/${siteConfig.bucketName}/${siteConfig.bucketPrefix}`
              : undefined
          }
        />
        <LockTile lockInfo={siteConfig.lock} />
        <InfoTile title='Urgency' value={siteConfig.publicMetadata?.urgency} />
      </div>

      {/* More Info Section */}
      <div css={{ marginBottom: theme.spacing(4) }}>
        <Button
          variant='tertiary'
          size='small'
          onClick={() => setShowMoreInfo(!showMoreInfo)}
          css={{ marginBottom: theme.spacing(3) }}
        >
          {showMoreInfo ? 'Hide' : 'Show'} More Information
        </Button>

        {showMoreInfo && (
          <div css={{ display: 'grid', gap: theme.spacing(2) }}>
            {siteConfig.publicMetadata && <JsonField label='Public Metadata' value={siteConfig.publicMetadata} />}
            {siteConfig.metadata && <JsonField label='Internal Metadata' value={siteConfig.metadata} />}
            {siteConfig.canaryConfig && <JsonField label='Canary Config' value={siteConfig.canaryConfig} />}
            <JsonField label='Full Config' value={siteConfig} />
          </div>
        )}
      </div>
    </div>
  );
};

const InfoTile = ({ title, value, href }: { title: string; value: string | undefined; href?: string }) => {
  const displayValue = value || 'N/A';

  return (
    <div
      css={{
        padding: theme.spacing(2),
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.small,
        border: `1px solid ${theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(1) }}>
        <Text weight='medium' color='subdued' css={{ fontSize: theme.fontSize(12) }}>
          {title}
        </Text>
      </div>
      <div>
        {href && value ? (
          <TextLink
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            css={{
              fontSize: theme.fontSize(14),
              fontWeight: 600,
              wordBreak: 'break-word',
            }}
          >
            {displayValue}
          </TextLink>
        ) : (
          <Text weight='semibold' css={{ fontSize: theme.fontSize(14), wordBreak: 'break-word' }}>
            {displayValue}
          </Text>
        )}
      </div>
    </div>
  );
};

const LockTile = ({ lockInfo }: { lockInfo: SiteInfo['lock'] }) => {
  const isLocked = !!lockInfo?.lockedAt;
  const reason = lockInfo?.reason;
  const lockedAt = lockInfo?.lockedAt;

  const getStatusText = () => {
    if (!isLocked) return 'No';
    if (lockedAt) {
      const formattedDate = new Date(lockedAt).toLocaleString();
      return `Yes - at ${formattedDate}`;
    }
    return 'Yes';
  };

  return (
    <div
      css={{
        padding: theme.spacing(2),
        backgroundColor: isLocked ? theme.colors.warning5 : theme.colors.white,
        borderRadius: theme.borderRadius.small,
        border: `1px solid ${isLocked ? theme.colors.warning20 : theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(1) }}>
        <Text weight='medium' color='subdued' css={{ fontSize: theme.fontSize(12) }}>
          Locked
        </Text>
      </div>
      <div>
        <Text weight='semibold' css={{ fontSize: theme.fontSize(14), wordBreak: 'break-word' }}>
          {getStatusText()}
        </Text>
        {isLocked && reason && (
          <div css={{ marginTop: theme.spacing(0.5) }}>
            <Text color='subdued' css={{ fontSize: theme.fontSize(12), wordBreak: 'break-word' }}>
              {reason}
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const ConfigSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div css={{ marginBottom: theme.spacing(6) }}>
      <Heading level={3} css={{ marginBottom: theme.spacing(3), color: theme.colors.neutral70 }}>
        {title}
      </Heading>
      <div css={{ display: 'grid', gap: theme.spacing(2) }}>{children}</div>
    </div>
  );
};

const JsonField = ({ label, value }: { label: string; value: object }) => {
  const jsonString = JSON.stringify(value, null, 2);

  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(3),
        backgroundColor: theme.colors.white,
        borderRadius: theme.borderRadius.medium,
        border: `1px solid ${theme.colors.neutral20}`,
        transition: 'box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <div css={{ marginBottom: theme.spacing(2) }}>
        <Text weight='medium' color='default'>
          {label}:
        </Text>
      </div>
      <div>
        <Text
          color='subdued'
          css={{
            fontFamily: 'monospace',
            fontSize: theme.fontSize(12),
            whiteSpace: 'pre-wrap',
            backgroundColor: theme.colors.neutral5,
            padding: theme.spacing(2),
            borderRadius: theme.borderRadius.small,
            border: `1px solid ${theme.colors.neutral10}`,
          }}
        >
          {jsonString}
        </Text>
      </div>
    </div>
  );
};
